import type {ChangeEvent} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {usePasswordInput} from './usePasswordInput';
import {VisibilityIcon, VisibilityOffIcon} from 'Images/icons';

/**
 * The props for the `PasswordInput` component.
 * Configures a password input field with an integrated visibility toggle.
 */
interface ComponentProps {
    /** HTML autocomplete attribute for the input. */
    autoComplete?: string;
    /** Change handler for the input. */
    onChange(e: ChangeEvent<HTMLInputElement>): void;
    /** Placeholder text for the input field. */
    placeholder?: string;
    /** Whether the field is required. */
    required?: boolean;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'PasswordInput'
     */
    testId?: string;
    /** The current value of the input. */
    value: string;
}

/**
 * A password input field with a toggle button to show/hide the password.
 * Renders a standard text input with an eye icon button on the right side.
 *
 * @param props              The component props.
 * @param props.autoComplete HTML autocomplete attribute.
 * @param props.onChange     Change handler.
 * @param props.placeholder  Placeholder text.
 * @param props.required     Whether the field is required.
 * @param props.testId       An optional test ID. Defaults to `'PasswordInput'`.
 * @param props.value        Current input value.
 * @returns A JSX element rendering the PasswordInput.
 *
 * @example
 * ```tsx
 * <PasswordInput autoComplete="current-password" value={pw} onChange={handleChange} />
 * ```
 */
const PasswordInput = ({
    autoComplete,
    onChange,
    placeholder,
    required,
    testId = 'PasswordInput',
    value
}: ComponentProps) => {
    const {inputType, isVisible, toggleVisibility} = usePasswordInput();

    return (
        <Wrapper data-cy={testId}>
            <Input
                autoComplete={autoComplete}
                data-cy={`${testId}-input`}
                placeholder={placeholder}
                required={required}
                type={inputType}
                value={value}
                onChange={onChange}
            />
            <ToggleButton
                aria-label={isVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                data-cy={`${testId}-toggle`}
                tabIndex={-1}
                type="button"
                onClick={toggleVisibility}
            >
                {isVisible
                    ? <VisibilityOffIcon height={20} width={20} />
                    : <VisibilityIcon height={20} width={20} />}
            </ToggleButton>
        </Wrapper>
    );
};

PasswordInput.displayName = 'PasswordInput';

export {PasswordInput};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    position: relative;
`;

const Input = styled.input`
    border: 1px solid ${({theme}) => theme.colors.inkDisabled};
    border-radius: 8px;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 400;
    padding: ${spacing(3)} ${spacing(4)};
    padding-right: ${spacing(12)};
    width: 100%;
`;

const ToggleButton = styled.button`
    align-items: center;
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.inkSecondary};
    cursor: pointer;
    display: flex;
    padding: ${spacing(2)};
    position: absolute;
    right: ${spacing(2)};
    top: 50%;
    transform: translateY(-50%);

    &:hover {
        color: ${({theme}) => theme.colors.inkPrimary};
    }
`;